// countryFlags.ts
// Utility for converting country codes to emoji flags or flag icons

/**
 * Global identifier for when a user doesn't specify a country
 */
export const GLOBAL_IDENTIFIER = 'GLOBAL';

/**
 * Convert ISO 3166-1 alpha-2 country code to flag emoji
 * This works by converting each letter to a Unicode Regional Indicator Symbol
 * which when put together form a flag emoji in supporting platforms
 * 
 * @param countryCode ISO 3166-1 alpha-2 country code (e.g., 'US', 'GB')
 * @returns Flag emoji for the country
 */
export const countryCodeToEmoji = (countryCode: string): string => {
  // For the UK, we need to use GB (Great Britain) as the country code for emoji
  if (countryCode === 'UK') {
    countryCode = 'GB';
  }

  // Normalize the country code to uppercase
  countryCode = countryCode.toUpperCase();
  
  // Check if we have a valid 2-letter country code
  if (!/^[A-Z]{2}$/.test(countryCode)) {
    return '🌍'; // Default globe for invalid codes
  }
  
  // Convert each letter to the corresponding Regional Indicator Symbol
  // 0x1F1E6 is the Unicode for "Regional Indicator Symbol Letter A"
  // So we add the distance from 'A' to the letter we want
  const firstLetter = (countryCode.codePointAt(0) ?? 65) - 65 + 0x1F1E6;
  const secondLetter = (countryCode.codePointAt(1) ?? 65) - 65 + 0x1F1E6;
  
  // Convert the codepoints to characters and return the flag emoji
  return String.fromCodePoint(firstLetter) + String.fromCodePoint(secondLetter);
};

/**
 * Map of common country code irregularities or special cases
 */
export const countryCodeMappings: Record<string, string> = {
  'UK': 'GB', // United Kingdom -> Great Britain
  'EL': 'GR', // Greece (sometimes uses EL)
};

/**
 * Complete list of all countries with their ISO 3166-1 alpha-2 codes
 * This includes all 249 countries, territories, and regions with official codes
 */
export interface CountryInfo {
  code: string;
  name: string;
}

export const ALL_COUNTRIES: CountryInfo[] = [
  { code: 'AF', name: 'Afghanistan' },
  { code: 'AX', name: 'Åland Islands' },
  { code: 'AL', name: 'Albania' },
  { code: 'DZ', name: 'Algeria' },
  { code: 'AS', name: 'American Samoa' },
  { code: 'AD', name: 'Andorra' },
  { code: 'AO', name: 'Angola' },
  { code: 'AI', name: 'Anguilla' },
  { code: 'AQ', name: 'Antarctica' },
  { code: 'AG', name: 'Antigua and Barbuda' },
  { code: 'AR', name: 'Argentina' },
  { code: 'AM', name: 'Armenia' },
  { code: 'AW', name: 'Aruba' },
  { code: 'AU', name: 'Australia' },
  { code: 'AT', name: 'Austria' },
  { code: 'AZ', name: 'Azerbaijan' },
  { code: 'BS', name: 'Bahamas' },
  { code: 'BH', name: 'Bahrain' },
  { code: 'BD', name: 'Bangladesh' },
  { code: 'BB', name: 'Barbados' },
  { code: 'BY', name: 'Belarus' },
  { code: 'BE', name: 'Belgium' },
  { code: 'BZ', name: 'Belize' },
  { code: 'BJ', name: 'Benin' },
  { code: 'BM', name: 'Bermuda' },
  { code: 'BT', name: 'Bhutan' },
  { code: 'BO', name: 'Bolivia' },
  { code: 'BQ', name: 'Bonaire, Sint Eustatius and Saba' },
  { code: 'BA', name: 'Bosnia and Herzegovina' },
  { code: 'BW', name: 'Botswana' },
  { code: 'BV', name: 'Bouvet Island' },
  { code: 'BR', name: 'Brazil' },
  { code: 'IO', name: 'British Indian Ocean Territory' },
  { code: 'BN', name: 'Brunei Darussalam' },
  { code: 'BG', name: 'Bulgaria' },
  { code: 'BF', name: 'Burkina Faso' },
  { code: 'BI', name: 'Burundi' },
  { code: 'CV', name: 'Cabo Verde' },
  { code: 'KH', name: 'Cambodia' },
  { code: 'CM', name: 'Cameroon' },
  { code: 'CA', name: 'Canada' },
  { code: 'KY', name: 'Cayman Islands' },
  { code: 'CF', name: 'Central African Republic' },
  { code: 'TD', name: 'Chad' },
  { code: 'CL', name: 'Chile' },
  { code: 'CN', name: 'China' },
  { code: 'CX', name: 'Christmas Island' },
  { code: 'CC', name: 'Cocos (Keeling) Islands' },
  { code: 'CO', name: 'Colombia' },
  { code: 'KM', name: 'Comoros' },
  { code: 'CG', name: 'Congo' },
  { code: 'CD', name: 'Congo, Democratic Republic of the' },
  { code: 'CK', name: 'Cook Islands' },
  { code: 'CR', name: 'Costa Rica' },
  { code: 'CI', name: 'Côte d\'Ivoire' },
  { code: 'HR', name: 'Croatia' },
  { code: 'CU', name: 'Cuba' },
  { code: 'CW', name: 'Curaçao' },
  { code: 'CY', name: 'Cyprus' },
  { code: 'CZ', name: 'Czechia' },
  { code: 'DK', name: 'Denmark' },
  { code: 'DJ', name: 'Djibouti' },
  { code: 'DM', name: 'Dominica' },
  { code: 'DO', name: 'Dominican Republic' },
  { code: 'EC', name: 'Ecuador' },
  { code: 'EG', name: 'Egypt' },
  { code: 'SV', name: 'El Salvador' },
  { code: 'GQ', name: 'Equatorial Guinea' },
  { code: 'ER', name: 'Eritrea' },
  { code: 'EE', name: 'Estonia' },
  { code: 'SZ', name: 'Eswatini' },
  { code: 'ET', name: 'Ethiopia' },
  { code: 'FK', name: 'Falkland Islands (Malvinas)' },
  { code: 'FO', name: 'Faroe Islands' },
  { code: 'FJ', name: 'Fiji' },
  { code: 'FI', name: 'Finland' },
  { code: 'FR', name: 'France' },
  { code: 'GF', name: 'French Guiana' },
  { code: 'PF', name: 'French Polynesia' },
  { code: 'TF', name: 'French Southern Territories' },
  { code: 'GA', name: 'Gabon' },
  { code: 'GM', name: 'Gambia' },
  { code: 'GE', name: 'Georgia' },
  { code: 'DE', name: 'Germany' },
  { code: 'GH', name: 'Ghana' },
  { code: 'GI', name: 'Gibraltar' },
  { code: 'GR', name: 'Greece' },
  { code: 'GL', name: 'Greenland' },
  { code: 'GD', name: 'Grenada' },
  { code: 'GP', name: 'Guadeloupe' },
  { code: 'GU', name: 'Guam' },
  { code: 'GT', name: 'Guatemala' },
  { code: 'GG', name: 'Guernsey' },
  { code: 'GN', name: 'Guinea' },
  { code: 'GW', name: 'Guinea-Bissau' },
  { code: 'GY', name: 'Guyana' },
  { code: 'HT', name: 'Haiti' },
  { code: 'HM', name: 'Heard Island and McDonald Islands' },
  { code: 'VA', name: 'Holy See' },
  { code: 'HN', name: 'Honduras' },
  { code: 'HK', name: 'Hong Kong' },
  { code: 'HU', name: 'Hungary' },
  { code: 'IS', name: 'Iceland' },
  { code: 'IN', name: 'India' },
  { code: 'ID', name: 'Indonesia' },
  { code: 'IR', name: 'Iran' },
  { code: 'IQ', name: 'Iraq' },
  { code: 'IE', name: 'Ireland' },
  { code: 'IM', name: 'Isle of Man' },
  { code: 'IL', name: 'Israel' },
  { code: 'IT', name: 'Italy' },
  { code: 'JM', name: 'Jamaica' },
  { code: 'JP', name: 'Japan' },
  { code: 'JE', name: 'Jersey' },
  { code: 'JO', name: 'Jordan' },
  { code: 'KZ', name: 'Kazakhstan' },
  { code: 'KE', name: 'Kenya' },
  { code: 'KI', name: 'Kiribati' },
  { code: 'KP', name: 'North Korea' },
  { code: 'KR', name: 'South Korea' },
  { code: 'KW', name: 'Kuwait' },
  { code: 'KG', name: 'Kyrgyzstan' },
  { code: 'LA', name: 'Lao People\'s Democratic Republic' },
  { code: 'LV', name: 'Latvia' },
  { code: 'LB', name: 'Lebanon' },
  { code: 'LS', name: 'Lesotho' },
  { code: 'LR', name: 'Liberia' },
  { code: 'LY', name: 'Libya' },
  { code: 'LI', name: 'Liechtenstein' },
  { code: 'LT', name: 'Lithuania' },
  { code: 'LU', name: 'Luxembourg' },
  { code: 'MO', name: 'Macao' },
  { code: 'MG', name: 'Madagascar' },
  { code: 'MW', name: 'Malawi' },
  { code: 'MY', name: 'Malaysia' },
  { code: 'MV', name: 'Maldives' },
  { code: 'ML', name: 'Mali' },
  { code: 'MT', name: 'Malta' },
  { code: 'MH', name: 'Marshall Islands' },
  { code: 'MQ', name: 'Martinique' },
  { code: 'MR', name: 'Mauritania' },
  { code: 'MU', name: 'Mauritius' },
  { code: 'YT', name: 'Mayotte' },
  { code: 'MX', name: 'Mexico' },
  { code: 'FM', name: 'Micronesia' },
  { code: 'MD', name: 'Moldova' },
  { code: 'MC', name: 'Monaco' },
  { code: 'MN', name: 'Mongolia' },
  { code: 'ME', name: 'Montenegro' },
  { code: 'MS', name: 'Montserrat' },
  { code: 'MA', name: 'Morocco' },
  { code: 'MZ', name: 'Mozambique' },
  { code: 'MM', name: 'Myanmar' },
  { code: 'NA', name: 'Namibia' },
  { code: 'NR', name: 'Nauru' },
  { code: 'NP', name: 'Nepal' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'NC', name: 'New Caledonia' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'NI', name: 'Nicaragua' },
  { code: 'NE', name: 'Niger' },
  { code: 'NG', name: 'Nigeria' },
  { code: 'NU', name: 'Niue' },
  { code: 'NF', name: 'Norfolk Island' },
  { code: 'MK', name: 'North Macedonia' },
  { code: 'MP', name: 'Northern Mariana Islands' },
  { code: 'NO', name: 'Norway' },
  { code: 'OM', name: 'Oman' },
  { code: 'PK', name: 'Pakistan' },
  { code: 'PW', name: 'Palau' },
  { code: 'PS', name: 'Palestine' },
  { code: 'PA', name: 'Panama' },
  { code: 'PG', name: 'Papua New Guinea' },
  { code: 'PY', name: 'Paraguay' },
  { code: 'PE', name: 'Peru' },
  { code: 'PH', name: 'Philippines' },
  { code: 'PN', name: 'Pitcairn' },
  { code: 'PL', name: 'Poland' },
  { code: 'PT', name: 'Portugal' },
  { code: 'PR', name: 'Puerto Rico' },
  { code: 'QA', name: 'Qatar' },
  { code: 'RE', name: 'Réunion' },
  { code: 'RO', name: 'Romania' },
  { code: 'RU', name: 'Russian Federation' },
  { code: 'RW', name: 'Rwanda' },
  { code: 'BL', name: 'Saint Barthélemy' },
  { code: 'SH', name: 'Saint Helena, Ascension and Tristan da Cunha' },
  { code: 'KN', name: 'Saint Kitts and Nevis' },
  { code: 'LC', name: 'Saint Lucia' },
  { code: 'MF', name: 'Saint Martin (French part)' },
  { code: 'PM', name: 'Saint Pierre and Miquelon' },
  { code: 'VC', name: 'Saint Vincent and the Grenadines' },
  { code: 'WS', name: 'Samoa' },
  { code: 'SM', name: 'San Marino' },
  { code: 'ST', name: 'Sao Tome and Principe' },
  { code: 'SA', name: 'Saudi Arabia' },
  { code: 'SN', name: 'Senegal' },
  { code: 'RS', name: 'Serbia' },
  { code: 'SC', name: 'Seychelles' },
  { code: 'SL', name: 'Sierra Leone' },
  { code: 'SG', name: 'Singapore' },
  { code: 'SX', name: 'Sint Maarten (Dutch part)' },
  { code: 'SK', name: 'Slovakia' },
  { code: 'SI', name: 'Slovenia' },
  { code: 'SB', name: 'Solomon Islands' },
  { code: 'SO', name: 'Somalia' },
  { code: 'ZA', name: 'South Africa' },
  { code: 'GS', name: 'South Georgia and the South Sandwich Islands' },
  { code: 'SS', name: 'South Sudan' },
  { code: 'ES', name: 'Spain' },
  { code: 'LK', name: 'Sri Lanka' },
  { code: 'SD', name: 'Sudan' },
  { code: 'SR', name: 'Suriname' },
  { code: 'SJ', name: 'Svalbard and Jan Mayen' },
  { code: 'SE', name: 'Sweden' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'SY', name: 'Syrian Arab Republic' },
  { code: 'TW', name: 'Taiwan' },
  { code: 'TJ', name: 'Tajikistan' },
  { code: 'TZ', name: 'Tanzania' },
  { code: 'TH', name: 'Thailand' },
  { code: 'TL', name: 'Timor-Leste' },
  { code: 'TG', name: 'Togo' },
  { code: 'TK', name: 'Tokelau' },
  { code: 'TO', name: 'Tonga' },
  { code: 'TT', name: 'Trinidad and Tobago' },
  { code: 'TN', name: 'Tunisia' },
  { code: 'TR', name: 'Turkey' },
  { code: 'TM', name: 'Turkmenistan' },
  { code: 'TC', name: 'Turks and Caicos Islands' },
  { code: 'TV', name: 'Tuvalu' },
  { code: 'UG', name: 'Uganda' },
  { code: 'UA', name: 'Ukraine' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'US', name: 'United States of America' },
  { code: 'UM', name: 'United States Minor Outlying Islands' },
  { code: 'UY', name: 'Uruguay' },
  { code: 'UZ', name: 'Uzbekistan' },
  { code: 'VU', name: 'Vanuatu' },
  { code: 'VE', name: 'Venezuela' },
  { code: 'VN', name: 'Viet Nam' },
  { code: 'VG', name: 'Virgin Islands (British)' },
  { code: 'VI', name: 'Virgin Islands (U.S.)' },
  { code: 'WF', name: 'Wallis and Futuna' },
  { code: 'EH', name: 'Western Sahara' },
  { code: 'YE', name: 'Yemen' },
  { code: 'ZM', name: 'Zambia' },
  { code: 'ZW', name: 'Zimbabwe' },
  // Add the global "country" option
  { code: GLOBAL_IDENTIFIER, name: 'Global' }
];

/**
 * Validate and correct country code if needed
 * 
 * @param countryCode The country code to validate and potentially correct
 * @returns A valid country code or the original code if no mapping exists
 */
export const getValidCountryCode = (countryCode: string): string => {
  const normalizedCode = countryCode.toUpperCase();
  return countryCodeMappings[normalizedCode] || normalizedCode;
};

/**
 * Get flag representation for a country code
 * 
 * @param countryCode ISO 3166-1 alpha-2 country code or GLOBAL_IDENTIFIER
 * @returns Emoji flag for the country or a globe icon for global/invalid codes
 */
export const getCountryFlag = (countryCode: string): string => {
  if (!countryCode || typeof countryCode !== 'string') {
    return '🌍';
  }
  
  // Check for global identifier
  if (countryCode.toUpperCase() === GLOBAL_IDENTIFIER) {
    return '🌍';
  }
  
  // Create a span with the flag emoji
  const validCode = getValidCountryCode(countryCode);
  const flagEmoji = countryCodeToEmoji(validCode);
  
  // Also add a console log to help debug
  console.log(`Converting ${countryCode} to flag: ${flagEmoji}`);
  
  return flagEmoji;
};

/**
 * Generate a URL to fetch the SVG flag for a country
 * This is a fallback option in case emoji flags don't render properly
 * 
 * @param countryCode ISO 3166-1 alpha-2 country code
 * @returns URL to the flag SVG image
 */
export const getFlagIconUrl = (countryCode: string): string => {
  if (!countryCode || typeof countryCode !== 'string') {
    return 'https://flagcdn.com/w20/globe.png'; // Default globe icon
  }

  // Check for global identifier
  if (countryCode.toUpperCase() === GLOBAL_IDENTIFIER) {
    return 'https://flagcdn.com/w20/globe.png'; // Default globe icon
  }

  // Handle special cases
  const validCode = getValidCountryCode(countryCode).toLowerCase();
  
  // Return a URL to the flag icon
  return `https://flagcdn.com/w20/${validCode.toLowerCase()}.png`;
};

/**
 * Get all unique country codes currently used in the application
 * Useful for preloading frequently used flags
 * 
 * @returns Array of country codes
 */
export const getCommonCountryCodes = (): string[] => {
  // These are the most commonly used country codes in the app
  // Can be expanded based on usage analytics
  return ['US', 'GB', 'CA', 'AU', 'DE', 'FR', 'JP', 'CN', 'BR', 'IN'];
};

/**
 * Sort countries alphabetically by name
 * 
 * @returns A sorted array of countries
 */
export const getSortedCountries = (): CountryInfo[] => {
  // Create a copy of the array to avoid modifying the original
  return [...ALL_COUNTRIES].sort((a, b) => {
    // Always place Global at the top
    if (a.code === GLOBAL_IDENTIFIER) return -1;
    if (b.code === GLOBAL_IDENTIFIER) return 1;
    
    // Regular alphabetical sort for the rest
    return a.name.localeCompare(b.name);
  });
};

/**
 * Find a country by its code
 * 
 * @param code The country code to search for
 * @returns The country info object or undefined if not found
 */
export const findCountryByCode = (code: string): CountryInfo | undefined => {
  if (!code) return undefined;
  
  const normalizedCode = code.toUpperCase();
  return ALL_COUNTRIES.find(country => country.code === normalizedCode);
};

/**
 * Find a country by its name (case-insensitive, partial match)
 * 
 * @param name The country name to search for
 * @returns The country info object or undefined if not found
 */
export const findCountryByName = (name: string): CountryInfo | undefined => {
  if (!name) return undefined;
  
  const normalizedName = name.toLowerCase();
  return ALL_COUNTRIES.find(country => 
    country.name.toLowerCase().includes(normalizedName)
  );
};

/**
 * Get the name of a country from its code
 * 
 * @param code The country code
 * @returns The country name or 'Unknown' if not found
 */
export const getCountryName = (code: string): string => {
  if (!code) return 'Unknown';
  
  if (code.toUpperCase() === GLOBAL_IDENTIFIER) {
    return 'Global';
  }
  
  const country = findCountryByCode(code);
  return country ? country.name : 'Unknown';
};
